import { X } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import "./ProductSelect.scss";
import Button from "../components/ui/Button";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import Dropdown from "../components/ui/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, openCart } from "../store/cartSlice";
import { getImageFromDB, saveImageToDB } from "../utils/indexedDB";
import { selectUploadedImageIds, setCustomizerStep } from "../store/customizerSlice";
import ProductDetailsModal from "../components/product/ProductDetailsModal";
import PetTypeSelector from "../components/product/PetTypeSelector";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { Oval } from 'react-loader-spinner';
import { useAffiliate } from "../hooks/useAffiliate";
import { useGetProductBundlesQuery, useGetProductsQuery } from "../store/apiSlice";
import BackMobileButton from "../components/ui/BackMobileButton";
import { getAffiliatePromoInfo } from "../utils/getAffiliatePromoInfo";
import ProductBox from "../components/product/ProductBox";
import useProductModal from "../hooks/useProductModal";
import { fbqTrack } from "../utils/eventsTracking/fbq";
import { gtagEvent } from "../utils/eventsTracking/gtag";
import { trackCTA } from "../utils/trackCTA";
import { trackCustomEvent } from "../utils/eventsTracking/trackCustomEvent";
import BundleBox from "../components/product/BundleBox";

const breadcrumbs = [
  { label: "Customizer", link: "/customizer" },
  { label: "Product Select", link: "/product-select" },
];

const ProductSelect = ({ isMobile }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { affiliate, isLoading: isLoadingAffiliate } = useAffiliate();

  const { data: products, isLoading } = useGetProductsQuery();
  const { data: bundles, isLoading: bundlesLoading } = useGetProductBundlesQuery();
  const allFlavors = (products || []).map(p => p.Flavor).filter(Boolean);
  const flavors = Array.from(new Set(allFlavors));

  const [finalImages, setFinalImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(false);
  const canvasRef = useRef(null);
  const [selectedFlavor, setSelectedFlavor] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [customizerStepCompleted, setCustomizerStepCompleted] = useState(false);
  const [selectedPetType, setSelectedPetType] = useState(() => localStorage.getItem("petType"));
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const [allImages, setAllImages] = useState([]);
  const submittedPromoCodes = useSelector(state => state.cart.submittedPromoCodes);
  const affiliateInfo = getAffiliatePromoInfo(affiliate, submittedPromoCodes);

  const {
    isOpen: isProductModalActive,
    modalData: productDetailsModalFor,
    openModal,
    closeModal
  } = useProductModal();

  const toggleSubscription = (productId) => {
    setSubscriptions((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  useEffect(() => {
    if (!selectedPetType) {
      navigate("/customizer");
    }
    dispatch(setCustomizerStep(2));
  }, []);


  const generateImagesForPetType = async (petType) => {
    if (!products || isLoading || !petType) return;

    setImagesLoading(true);
    const canvas = canvasRef.current;

    if (!canvas) {
      setImagesLoading(false);
      return;
    }

    const ctx = canvas.getContext("2d");
    let updatedImages = { ...finalImages };
    const resultImageId = localStorage.getItem("resultImageId");
    const resultImageBlob = await getImageFromDB("resultImages", resultImageId);

    if (resultImageBlob) {
      setCustomizerStepCompleted(true);
    }

    const petTypeProducts = products.filter(
      (product) => product.petType.toLowerCase() === petType.toLowerCase()
    );

    for (const product of petTypeProducts) {
      if (updatedImages[product.id]) {
        continue;
      }

      const onProductImage = await getImageFromDB("onProductImages", null, {
        productId: product.id,
        resultImageId
      });

      if (onProductImage) {
        updatedImages[product.id] = URL.createObjectURL(onProductImage);
        continue;
      }

      const mockupImageSrc = product.image;
      const mockupImage = new Image();
      mockupImage.src = mockupImageSrc;
      mockupImage.crossOrigin = "anonymous";

      await new Promise((resolve) => {
        mockupImage.onload = async () => {
          const imageBlob = await getImageFromDB("resultImages", resultImageId);

          if (!imageBlob) return resolve();

          const customizedImage = new Image();
          customizedImage.src = URL.createObjectURL(imageBlob);
          customizedImage.crossOrigin = "anonymous";

          customizedImage.onload = () => {
            const canvasWidth = mockupImage.width;
            const canvasHeight = mockupImage.height;
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.globalCompositeOperation = "source-over";
            ctx.drawImage(mockupImage, 0, 0, canvasWidth, canvasHeight);

            const overlayWidth = canvasWidth * 0.36;
            const overlayHeight = (overlayWidth * customizedImage.height) / customizedImage.width;
            const overlayX = (canvasWidth - overlayWidth) / 2;
            const overlayY = (canvasHeight - overlayHeight) / 1.65;
            ctx.drawImage(customizedImage, overlayX, overlayY, overlayWidth, overlayHeight);
            ctx.globalCompositeOperation = "source-over";

            canvas.toBlob(async (finalBlob) => {
              if (!finalBlob) return resolve();
              await saveImageToDB("onProductImages", `${product.id}_${resultImageId}`, finalBlob, product.id, resultImageId);
              updatedImages[product.id] = URL.createObjectURL(finalBlob);
              resolve();
            }, "image/png");
          };
        };
      });
    }

    setFinalImages(updatedImages);
    setImagesLoading(false);
  };

  useEffect(() => {
    if (selectedPetType) {
      generateImagesForPetType(selectedPetType);
    }
  }, [products, isLoading, selectedPetType]);


  // Filters
  const activeFilters = [
    selectedFlavor && selectedFlavor.length > 0 && { label: selectedFlavor, setter: setSelectedFlavor },
  ].filter(Boolean);

  const noFiltersApplied = !selectedFlavor?.length;

  const filteredProducts = (products || [])
    .filter((product) => product.petType.toLowerCase() === selectedPetType?.toLowerCase())
    .filter((product) => {
      if (noFiltersApplied) return true;
      const matchesFlavor = selectedFlavor.length === 0 || selectedFlavor.includes(product.flavor);
      return matchesFlavor;
    });

  const filteredBundles = (bundles || [])
    .filter((bundle) => bundle.petType?.toLowerCase() === selectedPetType?.toLowerCase())
    .filter((bundle) => {
      if (noFiltersApplied) return true;
      const hasMatchingFlavor = bundle.products.some(product =>
        selectedFlavor.length === 0 || selectedFlavor.includes(product.flavor)
      );
      return hasMatchingFlavor;
    });

  // Add to cart
  const handleAddToCart = async (
    product,
    isSubscribed = false,
    quantity = 1,
    bundleId = null,
    bundleName = null,
    bundleTotal = null,
    bundleRawTotal = null,
    bundleDiscount = null
  ) => {
    const resultImageId = localStorage.getItem("resultImageId");

    closeModal();

    dispatch(addToCart({
      ...product,
      quantity,
      subscription: isSubscribed,
      imageId: resultImageId,
      uploadedImageIds,
      bundleId,
      bundleName,
      bundleTotal,
      bundleRawTotal,
      bundleDiscount
    }));

    dispatch(openCart());

    // Events for marketing
    trackCustomEvent('added_to_cart', {
      sku: product.sku,
      price: product.price,
      title: product.title,
      flavor: product.flavor,
      quantity: quantity,
      subscription: isSubscribed,
      context: 'customized product'
    }, affiliate?.code || null);
    fbqTrack('AddToCart', {
      contents: [
        {
          id: product.sku,
          quantity: quantity,
          item_price: product.price,
          title: product.title,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ],
      content_ids: [product.sku],
      content_type: 'product',
      content_name: product.title,
      content_category: 'customized product',
      value: product.price * quantity,
      currency: 'USD',
      context: 'customized product'
    });
    gtagEvent('add_to_cart', {
      currency: 'USD',
      value: product.price * quantity,
      items: [
        {
          item_id: product.sku,
          item_name: product.title,
          item_category: 'customized product',
          quantity: quantity,
          price: product.price,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ]
    });
  };

  // const newImageButtonHandler = async () => {
  //   localStorage.removeItem("petType");
  //   localStorage.removeItem("petName");
  //   localStorage.removeItem("imageMetadata");
  //   localStorage.removeItem("label_template");
  //   resetIndexedDB("uploadedImages", cartItems);
  //   resetIndexedDB("resultImages", cartItems);
  //   resetIndexedDB("onProductImages", cartItems);
  //   dispatch(resetCustomizer());

  //   dispatch(setCustomizerStep(1));
  //   navigate("/customizer");
  // };

  const affiliateInfoText = affiliateInfo ?
    <div className="affiliateInfo text-sm md:text-base">
      <div className="flex justify-center items-center flex-col md:flex-row">
        <div className="image-col w-[50%] md:w-[20%] flex items-center p-5 justify-center">
          {affiliate?.logo &&
            <img src={affiliate.logo} alt="" className="w-full" />
          }
        </div>
        <div className="text-col pl-0 md:pl-5 w-[100%] md:w-[80%] text-md md:text-lg text-center md:text-left">
          <div>You've Unlocked Special Pricing!</div>You've unlocked a special offer exclusive to <span>{affiliateInfo.name}</span> fans! As a valued guest, you’re getting a <span>{affiliateInfo.discount_type === 'fixed' && '$'}{affiliateInfo.discount_amount}{affiliateInfo.discount_type === 'percent' && '%'}</span> discount on our premium pet products—handpicked to keep tails wagging and whiskers happy! <i>Valid for orders below ${affiliateInfo.max_order_total}</i>
        </div>
      </div>
    </div>
    : '';


  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen min-h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }


  return (
    <section className="product-select customizer-page min-h-screen full-width flex flex-col gap-10 items-center pt-5 md:pt-10">
      {!customizerStepCompleted && !finalImages ?
        <>
          <p className="color-swap text-mid">Please personalize the label first</p>
          <div>
            <Button type="button" variant="filled"
              onClick={() => {
                navigate("/customizer");
                trackCTA('Start Personalizing');
              }}
            >
              Start Personalizing
            </Button>
          </div>
        </>
        :
        <>
          <div className="flex flex-col gap-10 contained-width">
            {!isMobile && <Breadcrumbs items={breadcrumbs} />}
            <div className="flex flex-col gap-10 relative">

              {(affiliate && !affiliate?.auto_discount) && (
                <img
                  src={affiliate.logo}
                  alt={`${affiliate.name} logo`}
                  className="affiliate-logo"
                />
              )}

              {isMobile ?
                <div className={`flex flex-col gap-5 ${(affiliate && !affiliate?.auto_discount) ? 'affiliate-bottom-margin' : ''}`}>
                  <BackMobileButton onClick={() => {
                    dispatch(setCustomizerStep(2));
                    navigate('/customizer')
                  }} />
                  {affiliateInfoText && <div className="fade-step">{affiliateInfoText}</div>}
                  <h2 className={`text-center md:text-left text-3xl font-medium`}>Step 3: Choose Your Personalized Pet Product</h2>
                  <p className="text-center text-base md:text-mid mb-0">We’ve got all the goodies to keep your pets happy and healthy!</p>
                </div>
                :
                <>
                  {affiliateInfoText && <div className="fade-step">{affiliateInfoText}</div>}
                </>
              }


            </div>
          </div>
          <div className="contained-width flex flex-col gap-5">
            <div className={`flex justify-between w-full gap-5 flex-col md:flex-row relative`}>
              {!isMobile &&
                <div className="flex gap-3 flex-col md:flex-row items-center">
                  <PetTypeSelector
                    selected={selectedPetType}
                    setSelected={setSelectedPetType}
                  />
                  <Dropdown
                    label="Choose Flavor"
                    options={flavors}
                    selected={selectedFlavor}
                    setSelected={setSelectedFlavor}
                  />
                </div>
              }

              <h2 className={`text-center md:text-right capitalize color-swap ${(affiliate && !affiliate?.auto_discount) ? 'affiliate-margin' : ''}`}>{selectedPetType} Products</h2>

              {/* <div className="flex items-center gap-3 justify-center">
                <Button
                  type="button"
                  onClick={() => {
                    navigate('/customizer');
                    dispatch(setCustomizerStep(1));
                  }}
                  variant="outlined"
                  className="w-1/2 min-w-fit"
                >
                  <div className="flex items-center gap-2">
                    <Edit size={18} />
                    <span>Edit Image</span>
                  </div>
                </Button>

                <Button
                  type="button"
                  onClick={() => newImageButtonHandler()}
                  variant="outlined"
                  className="new-image-btn w-1/2 min-w-fit"
                >
                  <div className="flex items-center gap-2">
                    <Plus size={18} />
                    <span>New Image</span>
                  </div>
                </Button>
              </div> */}
            </div>

            {/* Active Filters */}
            {activeFilters.some((filter) => filter.label.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 mt-2 w-full justify-center md:justify-start">
                {activeFilters
                  .filter((filter) => filter.label.length > 0)
                  .map((filter, index) => (
                    <span key={index} className="filter-box flex items-center gap-2 px-3 py-1 rounded-full text-sm capitalize">
                      {Array.isArray(filter.label) ? filter.label.join(", ") : filter.label}
                      <button onClick={() => filter.setter([])}>
                        <X size={14} />
                      </button>
                    </span>
                  ))}
              </div>
            )}
          </div>

          <div className="product-boxes grid contained-width grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {/* {!bundlesLoading && filteredBundles.length > 0 && (
              filteredBundles.map((bundle) => (
                <BundleBox
                  key={bundle.id}
                  bundle={bundle}
                  products={bundle.products}
                  finalImages={finalImages}
                  openModal={openModal}
                  toggleSubscription={toggleSubscription}
                  handleAddToCart={handleAddToCart}
                  imagesLoading={imagesLoading}
                />
              ))
            )} */}
            {filteredProducts.length > 0 || filteredBundles.length > 0 ? (
              filteredProducts.map((product) => (
                <ProductBox
                  key={product.id}
                  product={product}
                  finalImages={finalImages}
                  subscriptions={subscriptions}
                  openModal={openModal}
                  toggleSubscription={toggleSubscription}
                  handleAddToCart={handleAddToCart}
                />
              ))
            ) : (
              <p className="text-gray-500 text-center col-span-full color-swap">No products or bundles match your filters.</p>
            )}
          </div>
        </>
      }

      <ProductDetailsModal
        isOpen={isProductModalActive}
        onClose={closeModal}
        product={productDetailsModalFor}
        handleAddToCart={handleAddToCart}
        allImages={allImages}
        setAllImages={setAllImages}
        image={
          productDetailsModalFor
            ? finalImages[productDetailsModalFor.id] || productDetailsModalFor.image
            : ""
        }
      />
      <canvas ref={canvasRef} className="hidden" />
    </section>
  );
};




export default ProductSelect;
